/**
 * The organisation's currency: what every amount in the console, the workbench and the phone is
 * shown in. The ledger stays in US dollars; the rate is the European Central Bank's for the day, or
 * one an administrator sets by hand for a deployment that cannot reach the feed.
 */

import { CURRENCIES, CURRENCY_NAMES, currencySymbol, type CurrencyCode, type OrganisationCurrency } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { useMoney } from '../money.tsx';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, selectClass, StatusMark } from './ui.tsx';

const day = (iso: string) => dateMedium(iso.length === 10 ? `${iso}T00:00:00Z` : iso);

export function CurrencyPanel() {
  const { money, reload } = useMoney();
  const profile = useLoad(() => routes.organisationProfile(), []);
  // Update on the organisation's settings (today: administrators), from what the person may do.
  const admin = { data: useMay('organisation.settings', 'update') };
  const [draft, setDraft] = useState<OrganisationCurrency>();
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const current: OrganisationCurrency = profile.data?.currency ?? { code: 'USD', rate: 'daily' };
  const d = draft ?? current;
  const save = async () => {
    setError(undefined);
    try {
      await routes.setOrganisationProfile({ currency: d.code === 'USD' && d.rate === 'daily' ? null : { code: d.code, rate: d.rate, ...(d.rate === 'fixed' ? { perUsd: d.perUsd } : {}) } });
      setDraft(undefined);
      setSaved(true);
      profile.reload();
      reload();
    } catch (e) {
      const b = (e as { body?: { message?: string } }).body;
      setError(b?.message ?? (e as Error).message);
    }
  };
  const rateLine =
    money.source === 'usd'
      ? 'Amounts are in US dollars, the currency model providers charge in.'
      : money.source === 'daily'
        ? `1 US$ = ${currencySymbol(money.currency)}${money.perUsd}, the European Central Bank's rate${money.asOf ? ` for ${day(money.asOf)}` : ''}.`
        : money.source === 'fixed'
          ? `1 US$ = ${currencySymbol(money.currency)}${money.perUsd}, set by hand${money.asOf ? ` on ${day(money.asOf)}` : ''}.`
          : `No rate for ${CURRENCY_NAMES[money.currency]} could be fetched, so amounts stay in US dollars until the feed answers or a rate is set by hand.`;
  return (
    <ListGroup id="currency" title="Currency" description="Every cost and budget in the console, the workbench and the phone is shown in this currency. The platform records spend in US dollars and converts at the day's rate.">
      <ListRow
        title="Currency"
        description={rateLine}
        control={
          admin.data ? (
            <ChoiceSelect
              label="Currency"
              className="w-60"
              value={d.code}
              options={Object.fromEntries(CURRENCIES.map((c) => [c, `${CURRENCY_NAMES[c]} (${c})`])) as Record<CurrencyCode, string>}
              onChange={(code) => (setSaved(false), setDraft({ ...d, code }))}
            />
          ) : undefined
        }
        value={admin.data ? undefined : `${CURRENCY_NAMES[current.code]} (${current.code})`}
      />
      {admin.data && d.code !== 'USD' ? (
        <ListRow
          title="Rate"
          control={
            <ChoiceSelect
              label="Rate"
              className="w-60"
              value={d.rate}
              options={{ daily: "The day's rate, from the European Central Bank", fixed: 'A rate I set' }}
              onChange={(rate) => (setSaved(false), setDraft({ ...d, rate, ...(rate === 'fixed' && !d.perUsd ? { perUsd: money.currency === d.code && money.perUsd !== 1 ? money.perUsd : undefined } : {}) }))}
            />
          }
        />
      ) : null}
      {admin.data && d.code !== 'USD' && d.rate === 'fixed' ? (
        <ListRow
          title={`${currencySymbol(d.code)} for one US dollar`}
          htmlFor="aioe-currency-rate"
          control={<input id="aioe-currency-rate" className={`${selectClass} w-32 tabular-nums`} type="number" min="0.000001" step="any" value={d.perUsd ?? ''} onChange={(e) => (setSaved(false), setDraft({ ...d, perUsd: e.target.value ? Number(e.target.value) : undefined }))} placeholder="1.52" />}
        />
      ) : null}
      {admin.data && (draft || saved) ? (
        <ListRow
          title={saved ? <StatusMark state="connected" label="Saved; every surface shows it from its next refresh" /> : <span className="font-normal text-muted">Not saved yet.</span>}
          control={
            <Button size="sm" isDisabled={!draft || (d.rate === 'fixed' && d.code !== 'USD' && !(d.perUsd && d.perUsd > 0))} onPress={() => void save()}>
              Save
            </Button>
          }
        />
      ) : null}
      {profile.error || error ? (
        <ListBlock>
          <ErrorNote error={profile.error ?? error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
